import { Avatar, getInitials } from "@components/ui/Avatar";
import { DataTableColumnHeader } from "@components/ui/data-table/DataTableColumnHeader";
import { ROUTES } from "@features/rh/config/constants";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { AlertTriangle, CheckCircle, Eye, XCircle } from "lucide-react";
import { createColumns } from "@/lib/table/columnFactory";
import EtatDemandeBadge from "../../components/EtatDemandeBadge";
import { selectColumn } from "./select";

/** Raw `/demande-absences` row shape (Personne/TypeDemande/EtatDemande relations). */
export interface DemandeAbsenceRow {
  [key: string]: unknown;
  id: number;
  personne?: {
    prenom?: string | null;
    nom?: string | null;
    email?: string | null;
    utilisateur?: { avatar?: string | null } | null;
  } | null;
  typeDemande?: { libelle?: string | null } | null;
  etatDemande?: { code?: string | null; libelle?: string | null } | null;
  dateDebut?: string | null;
  dateFin?: string | null;
  dateCreation?: string | null;
  nombreJours?: number | null;
  isUrgent?: boolean;
}

export interface HRAbsenceColumnsOptions {
  /** Called when the "view" button is clicked. */
  onView?: (demande: DemandeAbsenceRow) => void;
  /** Called when the "approve" button is clicked (SOUMISE only). */
  onApprove?: (demande: DemandeAbsenceRow) => void;
  /** Called when the "reject" button is clicked (SOUMISE only). */
  onReject?: (demande: DemandeAbsenceRow) => void;
  /** Whether the current user can validate absence requests. */
  canValidate?: boolean;
  /** TanStack Router navigate — falls back to ROUTES.RH.CONGES.VIEW when onView is absent. */
  navigate?: (options: { to: string; params: { id: number } }) => void;
}

/**
 * Columns factory for the HR absence requests table.
 *
 * Restored from the file deleted in 49c76507 (bug /conges/me) — the HR view
 * consumes the raw `/demande-absences` shape, distinct from the normalized
 * LeaveRequest columns used by the dashboard (`congesRequestsColumns`).
 */
export function getColumns({
  onView,
  onApprove,
  onReject,
  canValidate = false,
  navigate,
}: HRAbsenceColumnsOptions) {
  return createColumns<DemandeAbsenceRow>([
    selectColumn,
    // Employee
    {
      accessorKey: "personne.nom",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Employé" />,
      cell: ({ row }) => {
        const personne = row.original.personne;
        const initials = getInitials(`${personne?.prenom ?? ""} ${personne?.nom ?? ""}`);
        const avatarIri = personne?.utilisateur?.avatar;
        const avatarId = typeof avatarIri === "string" ? Number(avatarIri.split("/").pop()) : NaN;
        return (
          <div className="flex items-center gap-3">
            <Avatar
              avatar={Number.isFinite(avatarId) ? { id: avatarId } : undefined}
              fallback={initials}
              size="sm"
            />
            <div className="flex flex-col">
              <span className="font-medium">
                {personne?.prenom} {personne?.nom}
              </span>
              {personne?.email && (
                <span className="text-xs text-base-content/60">{personne.email}</span>
              )}
            </div>
          </div>
        );
      },
    },

    // Type
    {
      accessorKey: "typeDemande.libelle",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
      cell: ({ getValue }) => (getValue() as string | null) || "-",
    },

    // Date range
    {
      accessorKey: "dateDebut",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Période" />,
      cell: ({ row }) => {
        const { dateDebut, dateFin, nombreJours } = row.original;

        if (!dateDebut) {
          return <span>-</span>;
        }

        const debut = new Date(dateDebut);
        const fin = dateFin ? new Date(dateFin) : null;
        const debutTime = debut.getHours() + debut.getMinutes();
        const finTime = fin ? fin.getHours() + fin.getMinutes() : 0;
        const isJourneeComplete = debutTime === 0 && finTime === 0;
        const isSameDay =
          debut.toLocaleDateString("fr-FR") ===
          (fin ? fin.toLocaleDateString("fr-FR") : debut.toLocaleDateString("fr-FR"));

        const dateOpts = { locale: fr };

        return (
          <div className="flex flex-col">
            <span>
              {format(debut, "dd/MM/yyyy", dateOpts)}
              {!isJourneeComplete && isSameDay && (
                <>
                  {" "}
                  {format(debut, "HH:mm", dateOpts)} → {fin ? format(fin, "HH:mm", dateOpts) : "—"}
                </>
              )}
              {" → "}
              {fin ? format(fin, "dd/MM/yyyy", dateOpts) : "-"}
            </span>
            {nombreJours != null && (
              <span className="text-xs text-base-content/60">
                {!isJourneeComplete && isSameDay && fin
                  ? `${((fin.getTime() - debut.getTime()) / (1000 * 60 * 60))
                      .toFixed(1)
                      .replace(".0", "")}h`
                  : `${nombreJours} jour(s)`}
              </span>
            )}
          </div>
        );
      },
    },

    // Status with badge
    {
      accessorKey: "etatDemande.libelle",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Statut" />,
      cell: ({ row, getValue }) => {
        const etatCode = row.original.etatDemande?.code;
        const etatLibelle = getValue() as string | undefined;
        const isUrgent = row.original.isUrgent;

        return (
          <div className="flex items-center gap-2">
            <EtatDemandeBadge code={etatCode} libelle={etatLibelle} />
            {isUrgent && (
              <span className="badge badge-error badge-xs animate-pulse" title="Urgent">
                <AlertTriangle className="w-3 h-3" />
              </span>
            )}
          </div>
        );
      },
    },

    // Date de création
    {
      accessorKey: "dateCreation",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Créée le" />,
      cell: ({ getValue }) => {
        const date = getValue() as string | null | undefined;
        return date ? format(new Date(date), "dd/MM/yyyy", { locale: fr }) : "-";
      },
    },

    // Actions
    {
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      enableHiding: false,
      enableSorting: false,
      cell: ({ row }) => {
        const demande = row.original;
        const etatCode = demande.etatDemande?.code;
        const demandeId = demande.id;
        const canAct = canValidate && etatCode === "SOUMISE";

        return (
          <div className="flex items-center gap-1 flex-wrap">
            {/* View button - always available */}
            <button
              type="button"
              className="btn btn-xs btn-ghost btn-square"
              onClick={(e) => {
                e.stopPropagation();
                if (onView) {
                  onView(demande);
                } else if (navigate) {
                  navigate({ to: ROUTES.RH.CONGES.VIEW, params: { id: demandeId } });
                }
              }}
              title="Voir la demande"
            >
              <Eye className="w-4 h-4" />
            </button>

            {/* Approve button - only for SOUMISE state */}
            {canAct && (
              <button
                type="button"
                className="btn btn-xs btn-soft btn-success"
                onClick={(e) => {
                  e.stopPropagation();
                  onApprove?.(demande);
                }}
                title="Approuver la demande"
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Approuver
              </button>
            )}

            {/* Reject button - only for SOUMISE state */}
            {canAct && (
              <button
                type="button"
                className="btn btn-xs btn-soft btn-error"
                onClick={(e) => {
                  e.stopPropagation();
                  onReject?.(demande);
                }}
                title="Refuser la demande"
              >
                <XCircle className="w-3 h-3 mr-1" />
                Refuser
              </button>
            )}

            {/* Show text for already processed */}
            {etatCode !== "SOUMISE" && (
              <span className="text-xs text-base-content/50 italic">
                {etatCode === "APPROUVEE" && "Approuvée"}
                {etatCode === "REFUSEE" && "Refusée"}
                {etatCode === "ANNULEE" && "Annulée"}
              </span>
            )}
          </div>
        );
      },
    },
  ]);
}
